import * as api from '@/api'
import identityHandler from '@/core/identity-handler'
import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'

require('promise.prototype.finally').shim()

Vue.use(Vuex)

const state = {
  editorData: {},
  currentSection: '',
  isLoggedIn: false,
  isAdmin: false,
  username: null,
  currentTenant: null,
  showMessage: false,
  message: {},
  sections: [],
  userTenants: [],
  historyRecords: [],
  paging: {
    currentPage: 0,
    howMany: 15
  },
  dataToRestore: '',
  dataRestored: false,
  auditLogs: [],
  auditLogSelected: false,
  sectionSelected: false
}

const getters = {
  editorData: state => state.editorData,
  currentSection: state => state.currentSection,
  isLoggedIn: state => state.isLoggedIn,
  isAdmin: state => state.isAdmin,
  username: state => state.username,
  currentTenant: state => state.currentTenant,
  message: state => state.message,
  sections: state => state.sections,
  userTenants: state => state.userTenants,
  historyRecords: state => state.historyRecords,
  canShowMoreHistoryRecords: state =>
    state.historyRecords.length >= state.paging.currentPage * state.paging.howMany,
  dataToRestore: state => state.dataToRestore,
  dataRestored: state => state.dataRestored,
  auditLogs: state => state.auditLogs,
  canShowMoreAuditLogs: state =>
    state.auditLogs.length >= state.paging.currentPage * state.paging.howMany,
  auditLogSelected: state => state.auditLogSelected,
  sectionSelected: state => state.sectionSelected
}

const mutations = {
  clear(state) {
    state.editorData = {}
    state.currentSection = ''
    state.sections = []
    state.userTenants = []
    state.historyRecords = []
    state.paging.currentPage = 0
    state.dataToRestore = ''
    state.dataRestored = false
    state.auditLogs = []
    state.auditLogSelected = false
    state.sectionSelected = false
  },
  setCurrentSection(state, section) {
    state.currentSection = section
  },
  updateEditorData(state, val) {
    let newEditorData = {
      schema: JSON.parse(val[0].data.schema),
      values: JSON.parse(val[1].data.values),
      version: val[1].data.version
    }
    state.editorData = newEditorData
  },
  updateEditorValues(state, data) {
    state.editorData.values = data.values
    state.editorData.version = data.version
  },
  updateEditorSchema(state, schema) {
    state.editorData.schema = schema
  },
  valuesUpdated(state, success) {
    console.log('values updated: $success')
  },
  setIsLoggedIn(state, value) {
    state.isLoggedIn = value
  },
  setIsAdmin(state, value) {
    state.isAdmin = value
  },
  login(state) {
    identityHandler.mgr.signinRedirect()
  },
  logout(state) {
    identityHandler.logout()
    state.isLoggedIn = false
    state.isAdmin = false
    state.username = null
    state.currentTenant = null
  },
  setLogin(state, login) {
    state.isLoggedIn = true
    state.username = login.username
    state.currentTenant = login.tenant
  },
  setCurrentTenant(state, tenant) {
    state.currentTenant = tenant
  },
  setMessage(state, message) {
    state.message = message
  },
  setSections(state, sections) {
    state.sections = sections
  },
  setUserTenants(state, userTenants) {
    state.userTenants = userTenants
  },
  setHistoryRecords(state, values) {
    state.historyRecords = state.historyRecords.concat(values)
  },
  clearHistoryRecords(state) {
    state.historyRecords = []
  },
  incrementPageNumber(state) {
    state.paging.currentPage++
  },
  clearPageNumber(state) {
    state.paging.currentPage = 0
  },
  setDataToRestore(state, values) {
    state.dataToRestore = values
  },
  clearDataToRestore(state) {
    state.dataToRestore = ''
  },
  setDataRestored(state) {
    state.dataRestored = true
  },
  clearDataRestored(state) {
    state.dataRestored = false
  },
  setAuditLogs(state, values) {
    state.auditLogs = state.auditLogs.concat(values)
  },
  clearAuditLogs(state) {
    state.auditLogs = []
  },
  setAuditLogSelected(state) {
    state.auditLogSelected = true
  },
  clearAuditLogSelected(state) {
    state.auditLogSelected = false
  },
  setSectionSelected(state) {
    state.sectionSelected = true
  },
  clearSectionSelected(state) {
    state.sectionSelected = false
  }
}

const actions = {
  getEditorData({ commit, state }) {
    const current = state.currentSection
    const tenant = state.currentTenant

    return new Promise((resolve, reject) => {
      Promise.all([api.getSchema(current, tenant), api.getSchemaValues(current, tenant)])
        .then(response => {
          commit('updateEditorData', response)
          resolve(response)
        })
        .catch(error => {
          console.error('Could not fetch ' + current.key + ' Schema or Values', error)
          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            commit('login')
          }
          reject(error)
        })
    })
  },
  updateValues({ commit, dispatch, state }, editorValues) {
    const current = state.currentSection
    const tenant = state.currentTenant

    const jsonStringValues = JSON.stringify(editorValues, null, 2)
    const version = state.editorData.version

    return new Promise((resolve, reject) => {
      api
        .updateValues(current, tenant, jsonStringValues, version)
        .then(response => {
          commit('valuesUpdated', true)
          commit('updateEditorValues', { values: editorValues, version: response.data.version })
          resolve(response)
        })
        .catch(error => {
          if (error.response.status === 412) {
            dispatch('showMessage', {
              title: 'Save error',
              type: 'error',
              text:
                'Command failed: you are working on stale version of data, please reload and start again.'
            })
          }
          if (error.response.status === 304) {
            dispatch('showMessage', {
              title: 'Save',
              type: 'info',
              text: 'There are no changes in the document.'
            })
          }
          if (error.response.status === 403) {
            dispatch('showMessage', {
              title: 'Save error',
              type: 'error',
              text:
                'Access denied, you need to logout and login again. Your changes has not been saved'
            })
          }
          console.error('Could not Update ' + current + ' Values', error)
          commit('valuesUpdated', false)
          reject(error)
        })
    })
  },
  getSections({ commit, state }) {
    const tenant = state.currentTenant

    return new Promise((resolve, reject) => {
      api
        .getSections(tenant)
        .then(response => {
          commit('setSections', response.data)
          resolve(response)
        })
        .catch(error => {
          console.error('Could not fetch sections', error)
          if (error.response.status === 401) {
            commit('login')
          }
          reject(error)
        })
    })
  },
  getUserTenants({ commit }) {
    return new Promise((resolve, reject) => {
      api
        .getUserTenants()
        .then(response => {
          commit('setUserTenants', response.data.tenants)
          resolve(response)
        })
        .catch(error => {
          console.error('Could not fetch userTenants', error)
          if (error.response.status === 401) {
            commit('login')
          }
          reject(error)
        })
    })
  },
  getHistoryRecords({ commit, dispatch, state }, showMore = false) {
    if (!showMore) {
      commit('clearPageNumber')
      commit('clearHistoryRecords')
    }

    var request = {
      pageNumber: state.paging.currentPage,
      howMany: state.paging.howMany
    }

    return new Promise((resolve, reject) => {
      api
        .getHistoryRecords(request, state.currentSection, state.currentTenant)
        .then(response => {
          const historyRecords = response.data.historyRecords
          commit('setHistoryRecords', historyRecords)
          if (!showMore && historyRecords.length === 0) {
            dispatch('showMessage', {
              title: 'History',
              type: 'success',
              text: 'There are no history records yet'
            })
          }

          commit('incrementPageNumber')
          resolve(response)
        })
        .catch(error => {
          dispatch('showMessage', {
            title: 'History error',
            type: 'error',
            text: 'Something went wrong while fetching history records'
          })
          reject(error)
        })
    })
  },
  getAuditLogs({ commit, dispatch, state }, showMore = false) {
    if (!showMore) {
      commit('clearPageNumber')
      commit('clearAuditLogs')
    }

    const section = state.currentSection
    const tenant = state.currentTenant
    const pageNumber = state.paging.currentPage
    const howMany = state.paging.howMany

    return new Promise((resolve, reject) => {
      api
        .getAuditLogs(section, tenant, pageNumber, howMany)
        .then(response => {
          const auditLogs = response.data.auditLogs
          commit('setAuditLogs', auditLogs)
          if (!showMore && auditLogs.length === 0) {
            dispatch('showMessage', {
              title: 'Audit Logs',
              type: 'success',
              text: 'There are no audit logs yet'
            })
          }

          commit('incrementPageNumber')
          resolve(response)
        })
        .catch(error => {
          dispatch('showMessage', {
            title: 'Audit Logs',
            type: 'error',
            text: 'Something went wrong while fetching audit logs'
          })
          reject(error)
        })
    })
  },
  getAllSettings({ commit, state }) {
    return new Promise((resolve, reject) => {
      api
        .getAllSettings(state.currentTenant)
        .then(response => {
          resolve(response)
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  uploadSettings({ commit, state }, archiveFile) {
    return new Promise((resolve, reject) => {
      api
        .uploadSettings(archiveFile, state.currentTenant)
        .then(response => {
          resolve(response)
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  showMessage({commit}, messageData) {
    commit('setMessage', {
      type: messageData.type,
      text: messageData.text
    })

    Vue.notify({
      title: messageData.title,
      text: messageData.text,
      type: messageData.type,
      duration: 10000
    })
  }
}

export default new Vuex.Store({
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
  plugins: [createPersistedState({ storage: window.sessionStorage })]
})



// WEBPACK FOOTER //
// ./src/store/index.js